import MagicJump from '@/components/MagicJump';
import { env } from '@/utils/env';
import { EPlatform } from '@/utils/enums/platform-enum';
import { useEffect, useRef } from 'react';
import { useInView } from 'react-intersection-observer';
import type { JumpTarget } from '@/components/MagicJump';
import type { StyleHotProductMarketingEntryData } from '../types';

interface MarketingEntryAdSectionItem {
  pc_image?: string | null;
  wap_image?: string | null;
  jump?: JumpTarget;
}

interface MarketingEntryAdSectionData {
  items?: MarketingEntryAdSectionItem[];
  section_title?: string;
  section_subtitle?: string;
}

type RenderableMarketingEntryItem = MarketingEntryAdSectionItem & {
  imageSrc: string;
};

interface SectionProps {
  deviceType: EPlatform;
  data?: (StyleHotProductMarketingEntryData & MarketingEntryAdSectionData) | null;
  onTrackAdClick?: (index: number, extraProperties: Record<string, string>) => void;
  onTrackAdExposure?: (index: number, extraProperties: Record<string, string>) => void;
}

function resolveAssetUrl(path?: string): string {
  if (!path) {
    return '';
  }

  if (/^(https?:)?\/\//.test(path) || path.startsWith('blob:')) {
    return path;
  }

  if (path.startsWith('/admin/')) {
    return `${env.NEXT_PUBLIC_S3_URL}${path}`;
  }

  return path;
}

function MarketingEntryBanner({
  item,
  index,
  title,
  onTrackAdClick,
  onTrackAdExposure,
}: {
  item: RenderableMarketingEntryItem;
  index: number;
  title: string;
  onTrackAdClick?: SectionProps['onTrackAdClick'];
  onTrackAdExposure?: SectionProps['onTrackAdExposure'];
}) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.8 });
  const hasTrackedExposureRef = useRef(false);

  useEffect(() => {
    if (inView && !hasTrackedExposureRef.current) {
      hasTrackedExposureRef.current = true;
      onTrackAdExposure?.(index, getAdTrackProperties(item));
    }
  }, [inView, index, item, onTrackAdExposure]);

  return (
    <div ref={ref}>
      <MagicJump
        item={item.jump}
        className="block w-full overflow-hidden rounded-[4px]"
        aria-label={`${title} banner ${index + 1}`}
        onJumpClick={() => onTrackAdClick?.(index, getAdTrackProperties(item))}
      >
        <div className="relative w-full overflow-hidden rounded-[4px] bg-white">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={item.imageSrc}
            alt={title ? `${title} ${index + 1}` : `marketing entry ${index + 1}`}
            loading="lazy"
            decoding="async"
            className="block h-auto w-full"
          />
          <div className="pointer-events-none absolute bottom-0 left-0 h-[177px] w-full bg-gradient-to-b from-[rgba(0,0,0,0)] to-[rgba(0,0,0,0.33)]" />
        </div>
      </MagicJump>
    </div>
  );
}

function MarketingEntryBannerWap({
  item,
  index,
  title,
  onTrackAdClick,
  onTrackAdExposure,
}: {
  item: RenderableMarketingEntryItem;
  index: number;
  title: string;
  onTrackAdClick?: SectionProps['onTrackAdClick'];
  onTrackAdExposure?: SectionProps['onTrackAdExposure'];
}) {
  const { ref, inView } = useInView({ triggerOnce: true, threshold: 0.8 });
  const hasTrackedExposureRef = useRef(false);

  useEffect(() => {
    if (inView && !hasTrackedExposureRef.current) {
      hasTrackedExposureRef.current = true;
      onTrackAdExposure?.(index, getAdTrackProperties(item));
    }
  }, [inView, index, item, onTrackAdExposure]);

  return (
    <div ref={ref}>
      <MagicJump
        item={item.jump}
        className="block w-full overflow-hidden"
        aria-label={`${title} banner ${index + 1}`}
        onJumpClick={() => onTrackAdClick?.(index, getAdTrackProperties(item))}
      >
        <div className="relative w-full overflow-hidden bg-[#f0f0f0]">
          {/* eslint-disable-next-line @next/next/no-img-element */}
          <img
            src={item.imageSrc}
            alt={title ? `${title} ${index + 1}` : `marketing entry ${index + 1}`}
            loading="lazy"
            decoding="async"
            className="block h-auto w-full"
          />
          {index === 0 && (
            <div
              className="pointer-events-none absolute bottom-0 left-0 h-[64px] w-full"
              style={{
                background:
                  'linear-gradient(180deg, rgba(0, 0, 0, 0) 4.022%, rgba(0, 0, 0, 0.6) 99.922%)',
              }}
            />
          )}
        </div>
      </MagicJump>
    </div>
  );
}

function getAdTrackProperties(item: MarketingEntryAdSectionItem) {
  return {
    pits_jump_type: item.jump?.jump_type == null ? '' : `${item.jump.jump_type}`,
    pits_jump_content: item.jump?.Jump_target == null ? '' : `${item.jump.Jump_target}`,
  };
}

export default function Section({
  deviceType,
  data,
  onTrackAdClick,
  onTrackAdExposure,
}: SectionProps) {
  const title = data?.section_title || '';
  const subtitle = data?.section_subtitle || '';
  const isWeb = deviceType === EPlatform.WEB;
  const marketingImages = (data?.items || [])
    .map((item) => {
      const imageSrc = resolveAssetUrl(
        isWeb ? item?.pc_image || item?.wap_image : item?.wap_image || item?.pc_image,
      );
      return item && imageSrc ? { ...item, imageSrc } : null;
    })
    .filter((item): item is RenderableMarketingEntryItem => Boolean(item));

  if (marketingImages.length === 0) {
    return null;
  }

  if (!isWeb) {
    return (
      <section className="w-full bg-white px-[12px] pb-[20px] pt-[40px]">
        <div className="flex w-full flex-col items-center gap-[24px]">
          {(title || subtitle) && (
            <div className="flex w-full flex-col items-center gap-[8px] text-center text-[#282828] [word-break:break-word]">
              {title && (
                <h2 className="w-full truncate font-bs text-[22px] leading-[normal]">{title}</h2>
              )}
              {subtitle && (
                <p className="line-clamp-4 w-full font-br text-[12px] leading-[15px]">{subtitle}</p>
              )}
            </div>
          )}

          <div className="flex w-full flex-col gap-[12px]">
            {marketingImages.map((item, index) => (
              <MarketingEntryBannerWap
                key={item.imageSrc}
                item={item}
                index={index}
                title={title}
                onTrackAdClick={onTrackAdClick}
                onTrackAdExposure={onTrackAdExposure}
              />
            ))}
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="w-full bg-white px-[48px] pb-[20px] pt-[80px]">
      <div className="flex w-full flex-col items-center gap-[48px]">
        {(title || subtitle) && (
          <div className="flex w-[668px] flex-col items-center gap-[8px] text-center text-[#282828]">
            {title && (
              <h2 className="w-full truncate font-bs text-[48px] leading-[58px]">{title}</h2>
            )}
            {subtitle && (
              <p className="line-clamp-4 w-full font-br text-[16px] leading-[19px]">{subtitle}</p>
            )}
          </div>
        )}

        <div className="grid w-full grid-cols-2 gap-[16px]">
          {marketingImages.map((item, index) => (
            <MarketingEntryBanner
              key={item.imageSrc}
              item={item}
              index={index}
              title={title}
              onTrackAdClick={onTrackAdClick}
              onTrackAdExposure={onTrackAdExposure}
            />
          ))}
        </div>
      </div>
    </section>
  );
}
